import { Link } from "react-router-dom";
import { 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Grid,
  Paper
} from "@mui/material";
import { 
  Layers as LayersIcon,
  Extension as PuzzlePieceIcon,
  Smartphone as SmartphoneIcon,
  Route as RouteIcon,
  People as UsersIcon,
  ArrowForward as ArrowRightIcon
} from "@mui/icons-material";

export default function StaffTestPage() {
  return (
    <Box sx={{ padding: 3, space: 3 }}>
      {/* Hero Section */}
      <Paper
        sx={{
          background: 'linear-gradient(to right, #1976d2, #42a5f5)',
          color: 'white',
          borderRadius: 4,
          padding: { xs: 4, md: 6 },
          marginBottom: 4
        }}
      >
        <Box sx={{ maxWidth: '750px' }}>
          <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
            Welcome to NavigationApp
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
            Navigate seamlessly through our multi-section interface with professional design and responsive layouts.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Button 
              variant="contained" 
              sx={{ 
                bgcolor: 'white', 
                color: '#1976d2',
                '&:hover': { bgcolor: '#f5f5f5' },
                px: 3,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              Get Started
            </Button>
            <Button 
              variant="outlined" 
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '&:hover': { bgcolor: 'white', color: '#1976d2' },
                px: 3,
                py: 1.5,
                fontSize: '1.1rem'
              }}
            >
              Learn More
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Navigation Overview */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        {/* Section 1 Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            '&:hover': { 
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease-in-out'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  bgcolor: '#1976d2',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LayersIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Section 1
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Main content pages
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {['Page 1', 'Page 2', 'Page 3'].map((page, index) => (
                  <Link 
                    key={page}
                    to={`/section1/page${index + 1}`} 
                    style={{ textDecoration: 'none' }}
                  >
                    <Paper
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        bgcolor: '#f5f5f5',
                        '&:hover': { bgcolor: '#e0e0e0' },
                        transition: 'background-color 0.2s',
                        cursor: 'pointer'
                      }}
                    >
                      <Typography sx={{ fontWeight: 500 }}>
                        {page}
                      </Typography>
                      <ArrowRightIcon sx={{ fontSize: 16, color: '#666' }} />
                    </Paper>
                  </Link>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Section 2 Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            height: '100%',
            '&:hover': { 
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease-in-out'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  bgcolor: '#42a5f5',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <PuzzlePieceIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Section 2
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Component parts
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {['Part 1', 'Part 2', 'Part 3', 'Part 4'].map((part, index) => (
                  <Link 
                    key={part}
                    to={`/section2/part${index + 1}`} 
                    style={{ textDecoration: 'none' }}
                  >
                    <Paper
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1.5,
                        bgcolor: '#f5f5f5',
                        '&:hover': { bgcolor: '#e0e0e0' },
                        transition: 'background-color 0.2s',
                        cursor: 'pointer'
                      }}
                    >
                      <Typography sx={{ fontWeight: 500 }}>
                        {part}
                      </Typography>
                      <ArrowRightIcon sx={{ fontSize: 16, color: '#666' }} />
                    </Paper>
                  </Link>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Features Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{
                width: 48,
                height: 48,
                bgcolor: '#e8f5e8',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}>
                <SmartphoneIcon sx={{ color: '#4caf50', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Responsive Design
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Works seamlessly across all devices with mobile-first approach.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{
                width: 48,
                height: 48,
                bgcolor: '#f3e5f5',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}>
                <RouteIcon sx={{ color: '#9c27b0', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Client-Side Routing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Smooth page transitions with React Router integration.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{
                width: 48,
                height: 48,
                bgcolor: '#fff3e0',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}>
                <UsersIcon sx={{ color: '#ff9800', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Accessibility
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Built with ARIA labels and keyboard navigation support.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}